import { useBrainStore } from '@/state/brainStore';
import { withServerAuthoredWrites } from '@/state/persistenceBridge';
import { topupMoveRequestClearIntent, topupMoveRequestIntent } from '@/api/intents';
import type { TopupPlan } from '@/domain/types';
export { liveMoveRequest } from '@/domain/opsWorklist';

export type TopupMoveRequestOutcome =
  | { ok: true; requestedStartIso: string }
  | { ok: false; message: string };

/**
 * D01-17: the client wants to move an appointment that is inside 7 days. That's
 * Ops' call, so the Agent sends it to Ops with the date the client asked for
 * (one server intent; nothing moves, no money). The plan mirrors the request
 * into the store without a bridge PUT, so this tab shows "Sent to Ops" and Ops'
 * Top-up extensions list shows the date.
 */
export function sendTopupMoveToOps(planId: string, requestedStartIso: string): Promise<TopupMoveRequestOutcome> {
  return topupMoveRequestIntent(planId, requestedStartIso).then((res): TopupMoveRequestOutcome => {
    if (!res.ok) return { ok: false, message: res.message ?? "It wasn't sent to Ops. Try again." };
    const plan = useBrainStore.getState().topupPlans[planId];
    if (plan) {
      withServerAuthoredWrites([['topupPlans', plan.id]], () =>
        useBrainStore.getState().upsertTopupPlan({
          ...plan,
          moveRequestedStartIso: res.requestedStartIso,
          moveRequestedBy: res.requestedBy,
          moveRequestedAt: res.requestedAt,
          ...(res.requestedFromIso ? { moveRequestedFromIso: res.requestedFromIso } : {}),
        }),
      );
    }
    return { ok: true, requestedStartIso: res.requestedStartIso };
  });
}

/** D01-17: Ops has dealt with the request; it comes off the plan (server intent, mirrored). */
export function clearTopupMoveRequest(planId: string): Promise<boolean> {
  return topupMoveRequestClearIntent(planId).then((res) => {
    if (!res.ok) return false;
    const plan = useBrainStore.getState().topupPlans[planId];
    if (plan) {
      const {
        moveRequestedStartIso: _s,
        moveRequestedBy: _b,
        moveRequestedAt: _a,
        moveRequestedFromIso: _f,
        ...rest
      } = plan;
      withServerAuthoredWrites([['topupPlans', plan.id]], () => useBrainStore.getState().upsertTopupPlan(rest as TopupPlan));
    }
    return true;
  });
}
